// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Fetches an equipment's observed telemetry over a recent window and maps it
// into per-metric chart series. A 404 is "no telemetry published" and resolves
// to an empty series list; any other failure is exposed as `error` so the
// caller can say the telemetry is unavailable rather than absent. Polled on the
// twin telemetry cadence, since every caller titles the card "last N min".

import { useQuery } from '@tanstack/react-query';
import { getEquipmentObservations, toMetricSeries } from '../services/telemetryService';
import { ApiError } from '../services/api';
import { TWIN_POLL_INTERVAL_MS } from '../config/queryConfig';
import type { MetricSeries } from '../types';

const FIFTEEN_MINS_MS = 15 * 60 * 1000;
const NO_SERIES: MetricSeries[] = [];

export interface EquipmentTelemetryWindow {
  /** Size of the trailing window to fetch, in ms. Defaults to 15 minutes. */
  windowMs?: number;
  /** Aggregation resolution (ISO-8601 duration, e.g. PT1M). Defaults to PT1H. */
  resolution?: string;
}

export interface EquipmentTelemetryResult {
  /** Last successfully loaded series; empty before the first load or when none is published. */
  series: MetricSeries[];
  /** First load in flight (a background poll does not blank the chart). */
  loading: boolean;
  /** The last fetch failed for a reason other than 404. `series` is the last good load, if any. */
  error: Error | null;
  refetch: () => void;
}

export function useEquipmentTelemetry(
  siteId?: string,
  equipmentId?: string,
  { windowMs = FIFTEEN_MINS_MS, resolution = 'PT1H' }: EquipmentTelemetryWindow = {}
): EquipmentTelemetryResult {
  // Only the two ids gate the fetch. This used to also require an `orgId`,
  // which incidentally suppressed telemetry for users whose /me resolved no
  // organization; the upstream contract no longer takes orgId, so that guard
  // is gone deliberately — don't restore it.
  const query = useQuery({
    queryKey: ['equipment-telemetry', siteId ?? '', equipmentId ?? '', windowMs, resolution],
    enabled: !!siteId && !!equipmentId,
    refetchInterval: TWIN_POLL_INTERVAL_MS,
    queryFn: async (): Promise<MetricSeries[]> => {
      const end = new Date();
      const start = new Date(end.getTime() - windowMs);
      try {
        const envelope = await getEquipmentObservations(siteId as string, equipmentId as string, {
          start: start.toISOString(),
          end: end.toISOString(),
          resolution,
        });
        return toMetricSeries(envelope.data);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return NO_SERIES;
        throw err;
      }
    },
  });

  return {
    series: query.data ?? NO_SERIES,
    loading: query.isLoading,
    error: query.error,
    refetch: () => {
      query.refetch();
    },
  };
}
